import { useEffect, useRef } from 'react';
import type { ValidatedPinUser } from 'washday-sdk';
import { modals } from '@mantine/modals';
import { PasswordInput, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useValidateUserPIN } from '../users/mutations';

export interface CashierPinRequestOptions {
  showSuccessNotification?: boolean;
  modalId?: string;
  signal?: AbortSignal;
  purpose?: 'order-collection';
  storeId?: string;
}

export function useRequestCashierPinValidation() {
  const { mutateAsync: validatePin } = useValidateUserPIN();
  const pinCodeRef = useRef<HTMLInputElement>(null);
  const pendingRequests = useRef(new Set<() => void>());
  useEffect(() => () => {
    pendingRequests.current.forEach(cancel => cancel());
    pendingRequests.current.clear();
  }, []);
  const requestPin = (
    options: CashierPinRequestOptions = {}
  ): Promise<ValidatedPinUser | null> => {
    const { showSuccessNotification = true, modalId = 'request-pin-modal', signal } = options;
    if (signal?.aborted) return Promise.resolve(null);

    return new Promise((resolve) => {
      let settled = false;
      let validating = false;
      const finish = (
        user: ValidatedPinUser | null,
        close = false
      ) => {
        if (settled) return;
        settled = true;
        signal?.removeEventListener('abort', abort);
        pendingRequests.current.delete(abort);
        resolve(user);
        if (close) modals.close(modalId);
      };
      const abort = () => finish(null, true);
      signal?.addEventListener('abort', abort, { once: true });
      pendingRequests.current.add(abort);
      return ConfirmationModal({
        modalId,
        closeOnConfirm: false,
        title: 'Ingresa el PIN de usuario',
        children: (
          <Stack>
            <PasswordInput
              type="tel"
              autoFocus
              label="PIN de usuario que va a realizar la acción"
              placeholder="••••"
              maxLength={6}
              minLength={4}
              ref={pinCodeRef}
            />
          </Stack>
        ),
        onConfirm: async () => {
          if (settled || signal?.aborted || validating) return;
          const pin = pinCodeRef.current?.value || '';
          if (!/^\d{4,6}$/.test(pin)) {
            showNotification({
              title: 'PIN inválido',
              message: 'El PIN debe tener entre 4 y 6 números',
              color: 'red'
            });
            return;
          }

          validating = true;
          try {
            const res = await validatePin({
              pin,
              ...(options.purpose ? { purpose: options.purpose, storeId: options.storeId } : {})
            });
            if (settled) return;
            if (res?._id) {
              finish(res, true);

              if (showSuccessNotification) {
                showNotification({
                  title: 'PIN correcto',
                  message: `Se registraran las acciones con el usuario ${res.name}`,
                  color: 'green'
                });
              }
            } else {
              showNotification({
                title: 'PIN incorrecto',
                message: 'Verifica el PIN ingresado',
                color: 'red'
              });
            }
          } catch {
            finish(null, true);
          } finally {
            validating = false;
          }
        },
        onCancel: () => finish(null),
        onClose: () => finish(null)
      });
    });
  };

  return {
    requestPin
  };
}
